import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { VNG_DIALOG_DATA, VngDialogRef, VngSelectOption, VngTourService } from "@vengreso/angular-components";
import { RxwebValidators } from "@rxweb/reactive-form-validators";
import { distinctUntilChanged } from "rxjs/operators";

import { Router } from "@angular/router";
import { getPlanId } from "src/app/core/core-services/constants/plans.constants";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { CategoryRenameComponent } from "../../shortcuts/add-shortcut-category/category-rename/category-rename.component";
import { AddShortcutComponent } from "../../shortcuts/add-shortcut/add-shortcut.component";
import { CategorySelectComponent } from "../../shared/category-select/category-select.component";
import {
  ShortcutCategoryService,
  SHORTCUT_CATEGORIES,
} from "src/app/core/core-services/services/shortcut-category.service";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { DynamicStorage } from "src/app/core/core-services/decorators/dynamic-storage";
import { GlobalHookService } from "src/app/global-hook.service";

@Component({
    selector: "app-template-copy",
    templateUrl: "./template-copy.component.html",
    styleUrls: ["./template-copy.component.scss"],
    standalone: false
})
export class TemplateCopyComponent implements OnInit, OnDestroy {
  public form: FormGroup;
  @DynamicStorage(SHORTCUT_CATEGORIES) shortcutCategoryList: any[];
  // R5: mat-select + the old select-search plugin -> vng-select [searchable]="true".
  // categoryOptions is the flattened, data-driven option list (built once per
  // shortcutCategoryList change) — vng-select's own built-in substring search
  // now does the filtering that filterCtrl/filterCategories() used to do by
  // hand (same recipe as add-shortcut.component.ts).
  categoryOptions: VngSelectOption[] = [];
  @ViewChild("categorySelect") categorySelect: CategorySelectComponent;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dataService: DataService,
    private readonly notification: NotificationService,
    private readonly extensionService: ExtensionService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    public dialogRef: VngDialogRef<AddShortcutComponent>,
    public dialog: DialogService,
    private readonly router: Router,
    private readonly sharedData: SharedDataService,
    private readonly shortcutCategoryService: ShortcutCategoryService,
    private readonly tourService: VngTourService,
    private readonly globalHookService: GlobalHookService
  ) {}

  get isOnboarding() {
    return this.globalHookService.onboardingActive;
  }

  ngOnInit(): void {
    this.getShortcutCategory();

    this.sharedData.storage
      .observe(SHORTCUT_CATEGORIES)
      .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
      .subscribe((value) => {
        this.shortcutCategoryList = value;
        this.buildCategoryOptions();
      });

    this.buildCategoryOptions();

    this.form = this.fb.group({
      template_id: [this.data.data.id],
      category_id: [null, [RxwebValidators.required()]],
    });

    if (this.isOnboarding) {
      setTimeout(() => {
        this.tourService.initialize([
          {
            anchorId: "step18",
            content: "Organize your FlyPlates within your existing categories or create a new. Then click Save.",
            title: "Pick a Category",
            enableBackdrop: true,
            disablePageScrolling: true,
            // NO nextOnAnchorClick here. This step's anchor is the whole dialog
            // (`<div vngTourAnchor="step18">` wraps every field), not one
            // control, so "advance when the anchor is clicked" fired on any
            // click inside it — opening the category picker ended the tour,
            // which is the one thing this step is asking the user to do. The
            // step now ends where it says it does: on Save, or whenever the
            // dialog goes away (see ngOnDestroy).
            placement: {
              xPosition: "before",
              yPosition: "above",
            },
            // Same reasoning as step17: "Got it" only dismissed the step, so it
            // is hidden and Save is the way on. Single-step tour, so no Back
            // button is rendered either — matching master's `tour-last-step`.
            popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end fms-tour-hide-end",
          },
        ]);

        setTimeout(() => {
          this.tourService.start();
        }, 700);
      }, 50);
    }
  }

  // R5: flatten the category tree into data-driven vng-select options once
  // per shortcutCategoryList change. Indentation uses NBSP (\u00A0 escapes
  // below) — plain spaces collapse in rendered HTML text, NBSP does not.
  buildCategoryOptions(): void {
    const options: VngSelectOption[] = [];
    for (const cat of this.shortcutCategoryList || []) {
      options.push({ value: cat.id, label: cat.name });
      for (const sub1 of cat.sub_category_lv1 || []) {
        options.push({ value: sub1.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0" + sub1.name });
        for (const sub2 of sub1.sub_category_lv2 || []) {
          options.push({ value: sub2.id, label: "\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0" + sub2.name });
        }
      }
    }
    this.categoryOptions = options;
  }

  shortcutCategoryRename(data: any) {
    this.dialog.openDialogComponent(CategoryRenameComponent, {
      self: this,
      data,
      backdropClass: "dialog-category-backdrop",
    });
  }

  saveCategory(data) {
    this.dataService.post({ url: "v1/user/shortcut-category", data }).subscribe(
      (response: any) => {
        if (response && response.result) {
          this.extensionService.sendUpdateMessage();
          this.getShortcutCategory(true);
          this.form.patchValue({
            category_id: response.result,
          });
          this.categorySelect.closePanel();
          this.notification.toast("Category added successfully!");
        }
      },
      () => {}
    );
  }

  getShortcutCategory(reloadFromApi = false) {
    this.shortcutCategoryService.loadShortcutCategory({ reloadFromApi });
  }

  close(): void {
    this.dialogRef.close();
  }

  /**
   * step18's anchor is this dialog, so the popover has to go when the dialog
   * does. It used to be dismissed as a side effect of `nextOnAnchorClick` —
   * removed, because it also fired on every click inside the dialog. Doing it
   * here instead covers every exit: Save, Cancel, Escape, backdrop, navigation.
   */
  ngOnDestroy(): void {
    this.tourService.end();
  }

  getCategoryType(id: string) {
    for (const item of this.shortcutCategoryList) {
      if (item.id == id) {
        return "category";
      }
      if (item.sub_category_lv1) {
        for (const subLv1 of item.sub_category_lv1) {
          if (subLv1.id == id) {
            return "sub_categories_lv1";
          }
          if (subLv1.sub_category_lv2) {
            for (const subLv2 of subLv1.sub_category_lv2) {
              if (subLv2.id == id) {
                return "sub_categories_lv2";
              }
            }
          }
        }
      }
    }
  }

  copyToShortcut(data: any) {
    data.type = this.getCategoryType(data.category_id);
    this.dataService.post({ url: "v1/user/copy-shortcut/template/single", data }).subscribe({
      next: (response: any) => {
        if (response && response.result) {
          this.notification.toast("Template added successfully.");
          this.extensionService.sendUpdateMessage();
          if (this.router.url != "/advanced-search-result") {
            this.data.self.listService.getList(true);
          }
          this.data.self?.detectUpdates?.emit();
          this.close();
        }
      },
      error: (error) => {
        if (error.status == 403 && error.error?.error == "You are not authorized to add flyplates") {
          let planId = getPlanId();
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              self: this,
              type: "Flyplates",
              showTheStarter: planId === "Free",
              error: planId === "Free" ? "flyplatesFree" : "flyplatesStarter",
            },
            "800px"
          );
          return;
        }
      },
    });
  }
}
